import { rootModuleId } from '@app/const';
import { Design, DesignClass } from '@app/types';
import { filterNull } from '../array';
import { pickValues } from '../object';

/** Information about a @see DesignModule and whether it is used. */
export type ModuleUsedInfo = {
  moduleName: string;
  moduleId: string;
  used: boolean;
};

/**
 * Get the module info for each @see DesignModule in a @see Design.
 *
 * @note `null` values are used for module IDs that are used, but the module is missing.
 */
export function getModulesUsage(
  serialized: Design
): Record<string, ModuleUsedInfo | null> {
  // Modules IDs that are used and should be investigated for nested modules.
  let stack = ([] as (null | string | undefined)[]).concat(
    // Global settings.
    pickValues(serialized.globalErrorHandling ?? {}, [
      'errorHandling',
      'returningFromWaitModule',
      'sessionStart',
      'userHangUp'
    ]),
    pickValues(serialized.globalExceptionHandling ?? {}, [
      'exception',
      'timeout'
    ]),
    pickValues(serialized.globalLoopHandling ?? {}, [
      'inARow',
      'xAndOut'
    ]),
    // Global intents.
    serialized.globalIntents?.map((gi) => {
      return gi.module;
    }),
    // Entry module.
    rootModuleId
  ).filter((id) => {
    return !!id;
  }) as string[];
  const modules = new Map(serialized.components.modules.map((mod) => {
    return [mod.id, mod];
  }));

  const used = new Set<string>();
  const missing = new Set<string>();
  while (stack.length) {
    const currentModuleId = stack.pop()!;
    if (used.has(currentModuleId)) {
      continue;
    }

    const currentModule = modules.get(currentModuleId);
    if (!currentModule) {
      missing.add(currentModuleId);
      continue;
    }

    const { nodes } = currentModule.versions[1].flow;
    const toAdd = filterNull(nodes.map((node) => {
      return (node._class === DesignClass.module) ? (
        node.props.parentComponentID
      ) : null;
    }));
    stack = stack.concat(toAdd);

    used.add(currentModuleId);
  }

  return Object.fromEntries(
    ([] as [string, ModuleUsedInfo | null][]).concat(
      [...missing].map((id) => {
        return [id, null];
      }),
      serialized.components.modules.map(({
        id,
        name
      }) => {
        return [id, {
          moduleId: id,
          moduleName: name,
          used: used.has(id)
        }];
      })
    )
  );
}
